import { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  ScrollSectionId,
  SECTION_FOCUS_ORDER,
  SectionId
} from 'utils/constants/invoiceVerificationSections';
import { ConfidenceLevel, SectionStatus } from 'utils/enums/Confidence';
import { getSectionStatus } from 'utils/helpers/confidenceHelpers';

interface UseInvoiceSectionFocusParams {
  invoiceId: number | string | undefined;
  isLoading: boolean;
  sectionConfidence: Record<SectionId, ConfidenceLevel>;
  sectionHasError: Record<SectionId, boolean>;
  sectionShouldAutoExpand: Record<SectionId, boolean>;
}

const SECTION_ATTENTION_PRIORITY: Record<SectionStatus, number> = {
  [SectionStatus.ERROR]: 0,
  [SectionStatus.LOW_CONFIDENCE]: 1,
  [SectionStatus.NEEDS_REVIEW]: 2,
  [SectionStatus.NONE]: 3
};

export const useInvoiceSectionFocus = ({
  invoiceId,
  isLoading,
  sectionConfidence,
  sectionHasError,
  sectionShouldAutoExpand
}: UseInvoiceSectionFocusParams) => {
  const sectionElementsRef = useRef<
    Partial<Record<ScrollSectionId, HTMLElement | null>>
  >({});
  const sectionRefCallbacksRef = useRef<
    Partial<Record<ScrollSectionId, (node: HTMLElement | null) => void>>
  >({});
  const initialFocusInvoiceIdRef = useRef<number | string | undefined | null>(
    null
  );
  const pendingExpandedSectionRef = useRef<ScrollSectionId | null>(null);

  const getSectionRef = useCallback((sectionId: ScrollSectionId) => {
    const existingCallback = sectionRefCallbacksRef.current[sectionId];
    if (existingCallback) {
      return existingCallback;
    }

    const refCallback = (node: HTMLElement | null) => {
      sectionElementsRef.current[sectionId] = node;
    };
    sectionRefCallbacksRef.current[sectionId] = refCallback;

    return refCallback;
  }, []);

  const scrollToSection = useCallback((sectionId: ScrollSectionId) => {
    const section = sectionElementsRef.current[sectionId];
    if (!section) {
      return;
    }

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    section.scrollIntoView({
      block: 'start',
      behavior: prefersReducedMotion ? 'auto' : 'smooth'
    });
  }, []);

  const handleSectionExpanded = useCallback(
    (sectionId: ScrollSectionId) => {
      if (pendingExpandedSectionRef.current !== sectionId) {
        return;
      }

      pendingExpandedSectionRef.current = null;
      scrollToSection(sectionId);
    },
    [scrollToSection]
  );

  const firstAttentionSection = useMemo<SectionId | undefined>(() => {
    let result: SectionId | undefined;
    let resultPriority = 3;

    SECTION_FOCUS_ORDER.forEach((sectionId) => {
      if (!sectionShouldAutoExpand[sectionId]) {
        return;
      }

      const priority =
        SECTION_ATTENTION_PRIORITY[
          getSectionStatus(
            sectionConfidence[sectionId],
            sectionHasError[sectionId]
          )
        ];

      if (priority < resultPriority) {
        result = sectionId;
        resultPriority = priority;
      }
    });

    return result;
  }, [sectionConfidence, sectionHasError, sectionShouldAutoExpand]);

  useEffect(() => {
    if (isLoading || initialFocusInvoiceIdRef.current === invoiceId) {
      return;
    }

    initialFocusInvoiceIdRef.current = invoiceId;

    if (firstAttentionSection) {
      scrollToSection(firstAttentionSection);
    }
  }, [firstAttentionSection, invoiceId, isLoading, scrollToSection]);

  const handleSectionOpened = useCallback(
    (sectionId: ScrollSectionId) => {
      scrollToSection(sectionId);
    },
    [scrollToSection]
  );

  const handleSectionExpansionChange = useCallback(
    (sectionId: ScrollSectionId, isExpanding: boolean) => {
      if (isExpanding) {
        pendingExpandedSectionRef.current = sectionId;
        scrollToSection(sectionId);
      } else if (pendingExpandedSectionRef.current === sectionId) {
        pendingExpandedSectionRef.current = null;
      }
    },
    [scrollToSection]
  );

  return {
    getSectionRef,
    handleSectionExpansionChange,
    handleSectionExpanded,
    handleSectionOpened
  };
};
